import React, { useRef, useEffect, useState, useCallback, useMemo } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import {
  settingsDevicesSelector,
  settingsMuteSelector,
  settingsTrackStatusSelector,
  mobileCamerasAtom,
  microphonePermissionStateAtom,
  cameraPermissionStateAtom
} from "../../recoil/atoms/settings"

import {
  SettingsProps,
  SettingsContainerProps,
  TDeviceTypes,
  DeviceTypes,
  DevicePermissionState,
  DevicePermissionType
} from "./settings.interfaces"
import type { DropdownItem } from "../common/dropdown/dropdown.interfaces"
import SettingsView from "./settingsView"
import { SDKContext } from "../../context/SDKContext"
import { useLocalStorage } from "../../hooks/useLocalStorage"
import { v4 as uuidv4 } from "uuid"
import { MobileSettings } from "./mobile-setting"
import { ConsoleLogger } from "../../util/logger"
import useCameraAccessFailedToast from "../../hooks/useCameraAccessFailedToast"
import { CURRENT_URL } from "../../constants"
import useDeviceManagerChecker from "../../hooks/useDeviceManagerChecker"
import useConfig from "src/hooks/useConfig"
const logger = new ConsoleLogger()

// remote tracks objects from Agora
type TrackRefs = {
  [P in TDeviceTypes]: any
}

const useSavedDevice = () => {
  const [storage, parseStorageData, setLocalStorage] = useLocalStorage(
    "localUserDevicesSelected"
  )

  const savedDevices = useMemo(() => {
    if (storage) {
      const devicesFromLocalStorage = Object.values(parseStorageData) as {
        audioDeviceId: string
        audioDeviceLabel: string
        videoDeviceId: string
        videoDeviceLabel: string
        url: string
      }[]
      return devicesFromLocalStorage.find(
        localStorageElement => localStorageElement.url === CURRENT_URL
      )
    }
  }, [storage])

  return {
    setLocalStorage,
    savedMicrophoneId: savedDevices?.audioDeviceId,
    savedCameraId: savedDevices?.videoDeviceId
  }
}

const SettingsContainer: React.FC<SettingsContainerProps> = ({
  onSubmit,
  isShowFullView,
  isHomePage,
  isMobile
}) => {
  const { localCamEnabled, requestPermissionsOnJoinPageLoad } = useConfig()
  const [savedDevices, setSavedDevices] = useRecoilState(
    settingsDevicesSelector
  )
  const setMobileCameras = useSetRecoilState(mobileCamerasAtom)
  const [mutedDevices, setMutedDevices] = useRecoilState(settingsMuteSelector)
  const [, setSettingsTrackStatus] = useRecoilState(settingsTrackStatusSelector)
  const microphonePermissionState = useRecoilValue(
    microphonePermissionStateAtom
  )
  const cameraPermissionState = useRecoilValue(cameraPermissionStateAtom)

  const [volume, setVolume] = useState<number>(0)

  const [microphoneList, setMicrophoneList] = useState<DropdownItem[]>([])
  const [cameraList, setCameraList] = useState<DropdownItem[]>([])
  const LSRTC = window.LSRTC

  const videoTrackRef = useRef(null)
  const audioTrackRef = useRef(null)
  const isCamRequestInProgress = useRef(false)
  const isMicRequestInProgress = useRef(false)
  const interval = useRef(null)
  const videoContainerRef = useRef<HTMLDivElement>()
  const tracks: TrackRefs = useMemo(() => ({ audio: null, video: null }), [])
  const meetingClient = React.useContext(SDKContext)
  const showCameraAccessFailedToast = useCameraAccessFailedToast()
  const { checkDevicePermission, handleDevicePermissionError } =
    useDeviceManagerChecker()

  React.useEffect(() => {
    if (meetingClient) {
      if (
        requestPermissionsOnJoinPageLoad === true ||
        meetingClient?.connected()
      ) {
        if (localCamEnabled === true) requestCamera()
        requestMicrophone()
      }
    }
  }, [meetingClient])

  const toggleEnableMuteDevice = useCallback(
    async (type: TDeviceTypes, isMuted: boolean) => {
      if (tracks[type]) {
        await tracks[type].setEnabled(!isMuted)
      }

      if (!isHomePage) {
        return
      }

      switch (type) {
        case DeviceTypes.audio:
          meetingClient?.setJoinPageMicEnabled(!isMuted) // here?
          break

        case DeviceTypes.video:
          meetingClient?.setJoinPageCamEnabled(!isMuted)
          break
      }
    },
    [meetingClient, tracks, isHomePage]
  )

  const { savedMicrophoneId, savedCameraId, setLocalStorage } = useSavedDevice()

  const handleChangeDevice = useCallback(
    async (type: TDeviceTypes, device: DropdownItem) => {
      if (type === DeviceTypes.audio) {
        setLocalStorage(
          {
            [`devices-${uuidv4()}`]: {
              videoDeviceLabel: savedDevices?.video?.label,
              videoDeviceId: savedDevices?.video?.id,
              audioDeviceLabel: device.label,
              audioDeviceId: device.id,
              url: CURRENT_URL
            }
          },
          "rewrite"
        )

        try {
          if (tracks.audio) {
            await tracks.audio.setDevice(device.id)
          }
          meetingClient?.setJoinPageMicrophoneDeviceId(device.id)
          setSavedDevices({ [type]: device })
        } catch (error) {
          // show toast message
          logger.log("Error while setting microphone: ", error)
        }
      }

      if (type === DeviceTypes.video) {
        setLocalStorage(
          {
            [`devices-${uuidv4()}`]: {
              videoDeviceLabel: device.label,
              videoDeviceId: device.id,
              audioDeviceLabel: savedDevices?.audio?.label,
              audioDeviceId: savedDevices?.audio?.id,
              url: CURRENT_URL
            }
          },
          "rewrite"
        )

        try {
          if (tracks.video) {
            await tracks.video.setDevice(device.id)
          }
          meetingClient?.setJoinPageCameraDeviceId(device.id)
          setSavedDevices({ [type]: device })
        } catch (error) {
          // show toast message
          showCameraAccessFailedToast()
          logger.log("Error while setting camera: ", error)
        }
      }
    },
    [
      setSavedDevices,
      showCameraAccessFailedToast,
      tracks,
      setLocalStorage,
      savedDevices?.video?.label,
      savedDevices?.video?.id,
      savedDevices?.audio?.label,
      savedDevices?.audio?.id,
      meetingClient
    ]
  )

  const handleChangeMuteDevice = useCallback(
    async (type: TDeviceTypes, isMuted: boolean) => {
      // check permission do action
      try {
        await toggleEnableMuteDevice(type, isMuted)
        setMutedDevices({ [type]: isMuted })
      } catch (error) {
        logger.log("Error while change mute state: ", error)

        // if type video and trying to unmute the camera then show the toast message
        type === DeviceTypes.video && !isMuted && showCameraAccessFailedToast()
      }
    },
    [setMutedDevices, toggleEnableMuteDevice, showCameraAccessFailedToast]
  )

  const requestMicrophone = useCallback(async (isEnabled = true) => {
    let audioTrack: any

    let microphones: DropdownItem[] = []
    if (LSRTC?._rtc) {
      const devices = await LSRTC.getMicrophones()

      // get Microphone
      microphones = devices.map(micro => ({
        id: micro.deviceId,
        label: micro.label
      }))
    } else {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Get the audio track from the stream
      const audioTrack = stream.getAudioTracks()[0]
      const activeDeviceId = audioTrack.getSettings().deviceId

      const devices = await navigator.mediaDevices.enumerateDevices()

      // Filter out only the audio input devices (microphones)
      microphones = devices
        .filter(device => device.kind === "audioinput")
        .map(mic => {
          return { id: mic.deviceId, label: mic.label }
        })

      // Find the microphone with the matching device ID
      const activeMicrophone = devices.find(
        device =>
          device.kind === "audioinput" && device.deviceId === activeDeviceId
      )
    }
    setMicrophoneList(microphones)

    if (microphones.length === 0) {
      // if no microphone
      return
    }

    let selectedMicrophone = microphones[0]
    if (savedMicrophoneId) {
      selectedMicrophone =
        microphones.find(micro => micro.id === savedMicrophoneId) ??
        microphones[0]
    }

    // if audioTrack is present, then there is no need to create it or It is in progress
    if (audioTrackRef.current || isMicRequestInProgress.current) return
    isMicRequestInProgress.current = true

    try {
      audioTrack = await LSRTC.createMicrophoneAudioTrack({
        microphoneId: selectedMicrophone.id
      })
      meetingClient?.setJoinPageMicrophoneDeviceId(selectedMicrophone.id)

      logger.log("selectedTrackId", microphones, selectedMicrophone)
    } catch (error) {
      logger.log("audioTrackError", error)
    }

    setSavedDevices({
      audio: selectedMicrophone
    })

    if (!audioTrack) {
      try {
        audioTrack = await LSRTC.createMicrophoneAudioTrack()
      } catch (err) {
        isMicRequestInProgress.current = false
        handleDevicePermissionError(err, DevicePermissionType.microphone)
        return
      }
    }

    handleChangeMuteDevice(DeviceTypes.audio, !isEnabled)
    audioTrackRef.current = audioTrack
    tracks.audio = audioTrack

    clearInterval(interval.current)
    interval.current = setInterval(() => {
      const volumeLevel: number = audioTrack?.getVolumeLevel()
      setVolume(volumeLevel)
    }, 100)

    isMicRequestInProgress.current = false

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const requestCamera = useCallback(async (isEnabled = true) => {
    let videoTrack: any
    let deviceError: any = null

    const devices = await LSRTC.getCameras()
    const cameraDevices = devices.map(cam => ({
      id: cam.deviceId,
      label: cam.label
    }))
    setCameraList(cameraDevices)

    if (cameraDevices.length === 0) {
      // if no camera
      return
    }

    let selectedCamera = cameraDevices[0]
    if (savedCameraId) {
      selectedCamera =
        cameraDevices.find(cam => cam.id === savedCameraId) ?? cameraDevices[0]
    }

    if (cameraDevices.length > 1) {
      setMobileCameras({
        front: cameraDevices[0],
        back: cameraDevices[1]
      })
    }

    // if videoTrack is present, then there is no need to create new
    if (videoTrackRef.current || isCamRequestInProgress.current) return

    // set camera request progress true
    isCamRequestInProgress.current = true

    try {
      videoTrack = await LSRTC.createCameraVideoTrack({
        cameraId: selectedCamera?.id
      })
      meetingClient?.setJoinPageCameraDeviceId(selectedCamera?.id)

      setSavedDevices({ video: selectedCamera })
      logger.log("cameraDevices", cameraDevices, selectedCamera)
    } catch (error) {
      // traverse all the camera device to set as default
      for (let i = 0; i < cameraDevices.length; i++) {
        try {
          videoTrack = await LSRTC.createCameraVideoTrack({
            cameraId: cameraDevices[i].id
          })
          setSavedDevices({
            video: cameraDevices[i]
          })
          const selectedCam = cameraDevices[i]
          meetingClient?.setJoinPageCameraDeviceId(cameraDevices[i].id)
          setSavedDevices({ video: selectedCam })
          break
        } catch (error) {
          deviceError = error
          logger.log("Creating cam track failed.", error)
        }
      }
    }

    if (!videoTrack) {
      await handleDevicePermissionError(
        deviceError,
        DevicePermissionType.camera
      )
      isCamRequestInProgress.current = false
      return
    }

    videoTrackRef.current = videoTrack
    tracks.video = videoTrackRef.current

    if (videoContainerRef.current && videoTrack) {
      videoTrack.play(videoContainerRef.current)
      setSettingsTrackStatus(true)
      handleChangeMuteDevice(DeviceTypes.video, false)
    }

    isCamRequestInProgress.current = false

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const releaseMicrophoneDevice = () => {
    // handleChangeMuteDevice(DeviceTypes.audio, true)
    setSavedDevices({ audio: null })
    clearInterval(interval.current)
    setMicrophoneList([])
    if (audioTrackRef.current) {
      audioTrackRef.current?.stop()
      audioTrackRef.current?.setEnabled(false)
    }
    audioTrackRef.current = null
  }

  const releaseCameraDevice = () => {
    // handleChangeMuteDevice(DeviceTypes.video, true)
    setSavedDevices({ video: null })
    setCameraList([])
    if (videoTrackRef.current) {
      videoTrackRef.current?.stop()
      videoTrackRef.current?.setEnabled(false)
    }
    videoTrackRef.current = null
  }

  useEffect(() => {
    if (microphonePermissionState === DevicePermissionState.granted) {
      const user = (meetingClient as any).user
      requestMicrophone(user?.micEnabled ?? true)
    } else {
      handleChangeMuteDevice(DeviceTypes.audio, true)
    }

    return releaseMicrophoneDevice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microphonePermissionState])

  useEffect(() => {
    if (
      cameraPermissionState === DevicePermissionState.granted &&
      localCamEnabled === true
    ) {
      requestCamera()
    } else {
      handleChangeMuteDevice(DeviceTypes.video, true)
    }

    return releaseCameraDevice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraPermissionState, localCamEnabled])

  // useEffect(() => {
  //   (async () => {

  //     // mute all the device by default
  //     // handleChangeMuteDevice(DeviceTypes.audio, !isJoinPageMicEnabled)
  //     // handleChangeMuteDevice(DeviceTypes.video, !isJoinPageCamEnabled)

  //     try {
  //       // await LSRTC.getDevices()

  //       // Need this for ios devices
  //       // requestMicrophone()
  //       // requestCamera()
  //     } catch (error) {
  //       logger.log("Error while requesting device permission: ", error)
  //     }

  //   })()

  //   return () => {
  //     // clear the data when component leave
  //     releaseCameraDevice()
  //     releaseMicrophoneDevice()
  //   }
  // }, [])

  const props: SettingsProps = {
    onSubmit,
    devicesLists: {
      audio: microphoneList,
      video: cameraList
    },
    handleChangeDevice,
    async handleChangeMuteDevice(type, isMuted) {
      const hasPermission = !isMuted ? await checkDevicePermission(type) : true

      if (!hasPermission) return

      // check if device as been loaded, else load the device. Need this for ios device
      if (!savedDevices[type]) {
        if (type === DeviceTypes.audio) {
          await requestMicrophone()
        } else if (localCamEnabled === true) {
          await requestCamera()
        }
        return
      }

      handleChangeMuteDevice(type, isMuted)
    },
    currentAudio: savedDevices.audio,
    currentVideo: savedDevices.video,
    currentMutedDevices: mutedDevices,
    volume,
    isShowFullView,
    tracks,
    isHomePage,
    isMobile,
    devicePermissionDenied: {
      audio: microphonePermissionState === DevicePermissionState.denied,
      video: cameraPermissionState === DevicePermissionState.denied
    }
  }

  return isMobile ? (
    <MobileSettings {...props} ref={videoContainerRef} />
  ) : (
    <SettingsView {...props} ref={videoContainerRef} />
  )
}

export default SettingsContainer
